import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useFilters } from '../store';
import { api } from '../lib/api';
import { dayStats } from '../lib/dischargeDays';
import type { CoordinationResponse } from '../../shared/discharge';
import { DISCHARGE_DESTINATIONS as destinations, DISCHARGE_COLORS as colors } from './DischargeDays';
import { CoordinationHistogram } from './CoordinationHistogram';
import { CoordinationTrend } from './CoordinationTrend';

const departments = ['入退院調整', 'MSW', '連携室'] as const;
const departmentColors = ['#0d9488', '#2563eb', '#9333ea'];
const fmt = (n: number) => `${n.toLocaleString('ja-JP')}件`;
const days = (n: number | null) => n == null ? '—' : `${n.toFixed(1)}日`;

function DepartmentDays({ data }: { data: CoordinationResponse }) {
  const values = departments.map((department, index) => {
    const rows = data.distribution.filter(r => r.coordination_department === department)
      .map(r => ({ destination: department, days: r.acute_days, episodes: r.episodes }));
    return { department, color: departmentColors[index], stats: dayStats(rows) };
  });
  const max = Math.max(10, Math.ceil(Math.max(...values.map(v => v.stats.median ?? 0)) / 10) * 10);
  return <div className="mt-5">
    <h3 className="text-sm font-semibold">調整部署別・急性期日数</h3>
    <p className="text-xs text-slate-600 mt-1">棒の長さは中央値（日）。件数は原票の「調整部署」に記載された入院数です。</p>
    <div className="mt-3 space-y-3" role="img" aria-label="調整部署別の急性期日数中央値">
      {values.map(({ department, color, stats }) => <div key={department} className="grid grid-cols-[5.5rem_minmax(0,1fr)_6.5rem] gap-2 items-center text-sm">
        <span className="font-medium">{department}</span>
        <div className="h-7 bg-slate-100 rounded-sm overflow-hidden">
          <div className="h-full rounded-sm" style={{ width: `${100 * (stats.median ?? 0) / max}%`, backgroundColor: color }} />
        </div>
        <span className="text-right tabular-nums font-semibold">{days(stats.median)}</span>
        <span />
        <span className="text-xs text-slate-500">中央50%: {days(stats.p25)}〜{days(stats.p75)}</span>
        <span className="text-xs text-slate-500 text-right">{fmt(stats.n)}</span>
      </div>)}
      <div className="grid grid-cols-[5.5rem_minmax(0,1fr)_6.5rem] gap-2 text-xs text-slate-500"><span /><div className="flex justify-between"><span>0日</span><span>{max}日</span></div><span /></div>
    </div>
  </div>;
}

function SpecialtyMatrix({ data }: { data: CoordinationResponse }) {
  const [showAll, setShowAll] = useState(false);
  const groups = new Map<string, { code: string; name: string; total: number; byDepartment: Map<string, { median: number; episodes: number }> }>();
  for (const row of data.specialties) {
    let group = groups.get(row.dep_code);
    if (!group) {
      group = { code: row.dep_code, name: row.dep_name.replace(/^HP\)/, ''), total: 0, byDepartment: new Map() };
      groups.set(row.dep_code, group);
    }
    group.total += row.episodes;
    group.byDepartment.set(row.coordination_department, { median: row.median_acute_days, episodes: row.episodes });
  }
  const ordered = [...groups.values()].sort((a, b) => b.total - a.total || a.code.localeCompare(b.code));
  const visible = showAll ? ordered : ordered.slice(0, 10);
  return <div className="mt-7">
    <h3 className="text-sm font-semibold">DPC診療科 × 調整部署</h3>
    <p className="text-xs text-slate-600 mt-1">各マスは急性期日数の中央値（日）と台帳記載件数。色が濃いほど日数が長く、60日以上は同じ濃さです。</p>
    <div className="overflow-x-auto mt-3"><table className="w-full min-w-[640px] text-sm">
      <thead><tr className="bg-slate-50"><th className="p-2 text-left">DPC診療科</th><th className="p-2 text-right">計</th>{departments.map(dep => <th key={dep} className="p-2 text-center">{dep}</th>)}</tr></thead>
      <tbody>{visible.map(group => <tr key={group.code} className="border-t border-slate-100">
        <th className="p-2 text-left font-medium">{group.name}</th>
        <td className="p-2 text-right tabular-nums">{fmt(group.total)}</td>
        {departments.map(dep => {
          const value = group.byDepartment.get(dep);
          return <td key={dep} className="p-1 text-center tabular-nums">
            {value ? <div className="rounded-sm py-1 px-2" style={{ backgroundColor: `rgba(13,148,136,${0.10 + 0.48 * Math.min(value.median, 60) / 60})` }}>
              <div className="font-semibold">{days(value.median)}</div><div className="text-xs text-slate-700">{fmt(value.episodes)}</div>
            </div> : <span className="text-slate-400">—</span>}
          </td>;
        })}
      </tr>)}</tbody>
    </table></div>
    {ordered.length > 10 && <button type="button" className="mt-2 text-sm text-teal-700 hover:underline" onClick={() => setShowAll(v => !v)}>{showAll ? '上位10科に戻す' : `全${ordered.length}科を表示`}</button>}
    <p className="text-xs text-slate-500 mt-2">件数が少ないマスの中央値は変動しやすいため、件数と合わせて見てください。科の「計」は重複のない入院数です。</p>
  </div>;
}

function DestinationTable({ data }: { data: CoordinationResponse }) {
  return <div className="mt-7">
    <h3 className="text-sm font-semibold">調整部署別・退院先構成</h3>
    <div className="overflow-x-auto mt-3"><table className="w-full min-w-[650px] text-sm">
      <thead><tr className="bg-slate-50"><th className="text-left p-2">調整部署</th><th className="text-right p-2">台帳記載件数</th><th className="text-left p-2">退院先構成</th></tr></thead>
      <tbody>{departments.map(dep => {
        const rows = data.rows.filter(r => r.coordination_department === dep);
        const total = rows.reduce((n, r) => n + r.episodes, 0);
        return <tr key={dep} className="border-t border-slate-100"><th className="text-left p-2 font-medium whitespace-nowrap">{dep}</th><td className="text-right p-2 tabular-nums">{fmt(total)}</td><td className="p-2">
          {total > 0 ? <><div className="flex h-5 w-full rounded-sm overflow-hidden bg-slate-100" role="img" aria-label={`${dep}の退院先構成`}>{destinations.map((dest, i) => { const n = rows.filter(r => r.destination === dest).reduce((sum, r) => sum + r.episodes, 0); return n > 0 ? <div key={dest} title={`${dest} ${fmt(n)} (${(100 * n / total).toFixed(1)}%)`} style={{ width: `${100 * n / total}%`, backgroundColor: colors[i] }} /> : null; })}</div><div className="text-xs text-slate-500 mt-1">{destinations.filter(d => rows.some(r => r.destination === d)).map(d => { const n = rows.filter(r => r.destination === d).reduce((sum, r) => sum + r.episodes, 0); return `${d} ${fmt(n)} (${(100 * n / total).toFixed(1)}%)`; }).join(' ／ ')}</div></> : <span className="text-slate-400">記録なし</span>}
        </td></tr>;
      })}</tbody>
    </table></div>
  </div>;
}

export function DischargeCoordination() {
  const f = useFilters();
  const filters = { from: f.periodFrom, to: f.periodTo, yotei: f.yotei, dept: f.dischargeDept };
  const { data, error, isPending } = useQuery({ queryKey: ['discharge-coordination', filters], queryFn: () => api.dischargeCoordination(filters) });
  return <section className="card p-4">
    <h2 className="font-semibold">転院先台帳の調整部署</h2>
    <p className="text-xs text-slate-600 mt-1">急性期終了月・予定／緊急・DPC科は上の条件と共通です。「台帳記載件数」は原票の「調整部署」欄に部署名があり、最初の急性期入院と照合できた件数です。</p>
    {isPending ? <p className="text-sm mt-3">読み込んでいます…</p> : error ? <p role="alert" className="text-sm text-red-700 mt-3">調整部署データを取得できません：{error.message}</p> : data && <>
      <DepartmentDays data={data} />
      <CoordinationTrend rows={data.monthly} from={filters.from} to={filters.to} sourceFrom={data.meta.source_from} sourceTo={data.meta.source_to} />
      <CoordinationHistogram distribution={data.distribution} />
      <SpecialtyMatrix data={data} />
      <DestinationTable data={data} />
      <p className="text-xs text-slate-600 mt-3 leading-relaxed">原票は転院先台帳（{data.meta.source_from}〜{data.meta.source_to}）です。1入院の最初の急性期終了だけを集計し、原票の「調整部署」1欄に記録された部署を示します。複数部署の共同関与や全入院の介入率は表せません。中央値の差は患者構成の差を含み、部署の効果を示しません。連携室の後方連携は現在終了しています。</p>
    </>}
  </section>;
}
